import { useState, useEffect, useCallback, useRef, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Profile from "@/pages/Profile";
import PublicTripSummary from "@/pages/PublicTripSummary";
import PublicProfile from "@/pages/PublicProfile";
import PricingPage from "@/pages/PricingPage";
import FeedbackPage from "@/pages/FeedbackPage";
import AdminAnalytics from "@/pages/AdminAnalytics";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import InviteLandingPage from "@/pages/InviteLandingPage";
import VerifyEmail from "@/pages/VerifyEmail";
import FoundingExplorerPage from "@/pages/FoundingExplorerPage";
import ExplorePage from "@/pages/ExplorePage";
import PaywallDemo from "@/pages/PaywallDemo";
import SubscriptionSuccessPage from "@/pages/SubscriptionSuccessPage";
import HallOfFame from "@/pages/HallOfFame";
import TripDetailsPage from "@/pages/TripDetailsPage";
import GlobalRankTheme from "@/components/GlobalRankTheme";
import { FeatureProvider } from "@/hooks/useFeatures";
// InstallPrompt removed as requested
import Footer from "@/components/Footer";
import WhatsNewBanner from "@/components/WhatsNewBanner";
import useLiveOps, { emitLiveError } from "@/hooks/useLiveOps";
import LiveOpsErrorBoundary from "@/components/LiveOpsErrorBoundary";
import { BACKEND_URL, API } from "@/config/api";
export { API };

console.log(`[DuoLyst] Backend: ${BACKEND_URL}`);
console.log(`[DuoLyst] WEB_HOST: ${window.location.origin}`);
console.log(`[DuoLyst] BUILD_VERSION: 2026-04-14-v2`);
console.log(`[DuoLyst] isNativeApp: ${window.isNativeApp}`);
console.log(`[DuoLyst] nativePlatform: ${window.nativePlatform}`);

// Theme Context for Dark Mode
const ThemeContext = createContext(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return context;
};

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Axios interceptor setup
const setupAxiosInterceptors = (token, logout) => {
  axios.defaults.headers.common['Authorization'] = token ? `Bearer ${token}` : '';

  // Always send the browser's IANA timezone so the backend can schedule
  // local-time pushes (e.g. "Europe/London", "America/New_York").
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz) axios.defaults.headers.common['X-Timezone'] = tz;
  } catch (e) {
    // non-critical — backend falls back to UTC
  }

  // Clear any existing interceptors to avoid duplicates
  axios.interceptors.response.handlers = [];
  
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      // Only logout if it's a 401 on the /auth/me endpoint (token validation)
      // Don't logout for other API calls that might fail with 401
      const isAuthEndpoint = error.config?.url?.includes('/auth/me');
      if (error.response?.status === 401 && isAuthEndpoint) {
        logout();
        toast.error("Session expired. Please login again.");
      }
      // Live Ops: capture 4xx/5xx as api_failure (never the live-ops endpoints
      // themselves — would create a feedback loop).
      try {
        const status = error.response?.status;
        const url = error.config?.url || "";
        const isLiveOpsEndpoint = url.includes("/live/ping") || url.includes("/live/event") || url.includes("/live/error");
        if (status && status >= 400 && !isLiveOpsEndpoint) {
          // Pull endpoint pathname relative to host
          let endpoint = url;
          try {
            const u = new URL(url, window.location.origin);
            endpoint = u.pathname;
          } catch (_) {}
          // Lazy import to avoid React Router circular import on first paint
          import("@/hooks/useLiveOps").then(({ emitLiveError }) => {
            emitLiveError("api_failure", error, {
              endpoint,
              status,
              method: (error.config?.method || "get").toUpperCase(),
              duration_ms: error.config?._startTime ? Date.now() - error.config._startTime : null,
            });
          }).catch(() => {});
        }
      } catch (_) { /* swallow */ }
      return Promise.reject(error);
    }
  );

  // Stamp request start time for duration_ms calc above
  axios.interceptors.request.use((config) => {
    config._startTime = Date.now();
    return config;
  });
};

// Auth Provider
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  // Get token from localStorage (always persist login)
  const [token, setToken] = useState(() => {
    return localStorage.getItem("token");
  });
  const [loading, setLoading] = useState(true);

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("user");
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
    
    // Clear IndexedDB offline data to prevent data leakage between users
    import('./lib/offlineDB.js').then(({ clearAllOfflineData }) => {
      clearAllOfflineData().catch(err => console.error('Failed to clear offline data:', err));
    });
  };

  useEffect(() => {
    setupAxiosInterceptors(token, logout);
    
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // Login page will process the token from the hash fragment.
    if (window.location.hash?.includes('google_token=') || window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    
    const authStartTs = Date.now();
    
    if (token) {
      // Verify token and get user.
      //
      // CRITICAL: only logout on a *real* auth failure (401/403 with a valid
      // server response). Network errors, timeouts, and 5xx (which happen
      // every time the backend cold-starts on Railway, or whenever Cloudflare
      // briefly 502s) must NEVER log the user out — they keep their session
      // and we just retry on the next route. Logging out on transient
      // upstream failures was the cause of the "everyone gets kicked out
      // when the backend comes back up" bug.
      axios.get(`${API}/auth/me`, { timeout: 15000 })
        .then((res) => {
          setUser(res.data);
          localStorage.setItem("user", JSON.stringify(res.data));
        })
        .catch((err) => {
          const status = err?.response?.status;
          // Only treat a definitive auth failure as a logout signal.
          if (status === 401 || status === 403) {
            logout();
          } else {
            // Cold-start / transient — hydrate from cached user (if any)
            // so the UI doesn't bounce to /login. Token stays; next nav
            // will retry. Console-log so we have a footprint in Live Ops.
            console.warn("[auth] /me transient failure, keeping session:", err?.message || err?.code);
            const cachedUser = localStorage.getItem("user");
            if (cachedUser) {
              try { setUser(JSON.parse(cachedUser)); } catch (_) {}
            }
          }
        })
        .finally(() => {
          setLoading(false);
          const authMs = Date.now() - authStartTs;
          console.log(`[DuoLyst] auth_bootstrap_ms: ${authMs}`);
        });
    } else {
      setLoading(false);
    }
  }, [token]);

  // Streak heartbeat — fires record-visit at most once per calendar day per
  // browser session. Moved out of StreakBadge.jsx so pages that don't render
  // the badge (ex: /profile, /settings) still tick the streak.
  const lastRecordRef = useRef(null);
  useEffect(() => {
    if (!token) return;
    const fireRecordVisit = async () => {
      // Use the user's LOCAL calendar date as the throttle key so a visit
      // just past local midnight always fires record-visit (matches the
      // backend's local-tz rollover in shared/streak_protection.py).
      // `toLocaleDateString('en-CA')` outputs YYYY-MM-DD in local time.
      const todayLocal = new Date().toLocaleDateString('en-CA');
      if (lastRecordRef.current === todayLocal) return;
      try {
        await axios.post(`${API}/auth/record-visit`);
        // CRITICAL: only mark "fired today" if the POST actually succeeded.
        // Cost us Dexter's 73-day streak when his Sunday call failed
        // transiently and the throttle locked him out for the rest of the day.
        lastRecordRef.current = todayLocal;
      } catch (_) {
        // leave ref unchanged so the next visibility / nav event retries
      }
    };
    fireRecordVisit();
    const onVis = () => { if (document.visibilityState === 'visible') fireRecordVisit(); };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [token]);

  // Re-validate auth when app returns to foreground (tab visible or native foregrounded)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && token) {
        axios.get(`${API}/auth/me`)
          .then((res) => {
            setUser(res.data);
            localStorage.setItem("user", JSON.stringify(res.data));
          })
          .catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    // Also listen for native foregrounded event
    const handleForegrounded = () => {
      if (token) {
        axios.get(`${API}/auth/me`)
          .then((res) => {
            setUser(res.data);
            localStorage.setItem("user", JSON.stringify(res.data));
          })
          .catch(() => {});
      }
    };
    window.addEventListener('duolyst:refresh-entitlements', handleForegrounded);
    window.addEventListener('duolyst:user-updated', handleForegrounded);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('duolyst:refresh-entitlements', handleForegrounded);
      window.removeEventListener('duolyst:user-updated', handleForegrounded);
    };
  }, [token]);

  // Auto-accept partner invite after auth
  const checkPendingInvite = async (authToken) => {
    const pendingToken = localStorage.getItem("duolyst_invite_token");
    if (!pendingToken) return null;
    try {
      const res = await axios.post(
        `${API}/invite/accept`,
        { token: pendingToken },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      localStorage.removeItem("duolyst_invite_token");
      return res.data;
    } catch (e) {
      localStorage.removeItem("duolyst_invite_token");
      return null;
    }
  };

  // ── Centralized post-auth pipeline ──
  // All providers (Apple, Google, Email) must call this after getting a token+user.
  // This ensures: token persisted → auth state updated → axios configured → navigation ready.
  const persistAndHydrateSession = useCallback(async (newToken, userData) => {
    localStorage.setItem("token", newToken);
    localStorage.setItem("user", JSON.stringify(userData));
    setToken(newToken);
    setUser(userData);
    setupAxiosInterceptors(newToken, logout);
    
    // Auto-accept pending invite
    const inviteResult = await checkPendingInvite(newToken);
    if (inviteResult) {
      try {
        const updated = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${newToken}` } });
        setUser(updated.data);
        localStorage.setItem("user", JSON.stringify(updated.data));
      } catch (e) {}
    }
    
    return { ...userData, inviteResult };
  }, []);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password }, { timeout: 20000 });
    const { token: newToken, user: userData } = res.data;
    return persistAndHydrateSession(newToken, userData);
  };

  const register = async (name, email, password, referralCode = null) => {
    const signupSource = window.isNativeApp
      ? (window.nativePlatform === 'ios' ? 'ios' : window.nativePlatform === 'android' ? 'android' : 'web')
      : 'web';
    const body = { name, email, password, signup_source: signupSource };
    if (referralCode) body.referral_code = referralCode;
    const res = await axios.post(`${API}/auth/register`, body, { timeout: 20000 });
    const { token: newToken, user: userData } = res.data;
    return persistAndHydrateSession(newToken, userData);
  };

  const refreshUser = async () => {
    const res = await axios.get(`${API}/auth/me`);
    setUser(res.data);
    localStorage.setItem("user", JSON.stringify(res.data));
    return res.data;
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, loading, refreshUser, persistAndHydrateSession }}>
      {children}
    </AuthContext.Provider>
  );
};

// Branded Loading Shell — dark skeleton that matches startup-shell in index.html
// Prevents white flash between HTML shell removal and React render
const LoadingShell = () => (
  <div className="min-h-screen flex flex-col bg-[#0B0F1A] text-white" data-testid="loading-shell">
    {/* Top nav skeleton */}
    <div className="h-14 flex items-center px-4 gap-3 border-b border-white/[0.06]">
      <div className="w-7 h-7 rounded-full bg-white/[0.08]" />
      <div className="w-20 h-3.5 rounded-full bg-white/[0.08]" />
      <div className="flex-1" />
      <div className="w-20 h-8 rounded-2xl bg-[rgba(224,122,95,0.25)]" />
    </div>
    {/* Card skeletons */}
    <div className="flex-1 p-4 space-y-3">
      <div className="h-[120px] rounded-2xl bg-white/[0.04] animate-pulse" />
      <div className="h-[180px] rounded-2xl bg-white/[0.04] animate-pulse" style={{ animationDelay: '0.1s' }} />
      <div className="h-12 rounded-xl bg-white/[0.04] animate-pulse" style={{ animationDelay: '0.2s' }} />
      <div className="h-[100px] rounded-2xl bg-white/[0.04] animate-pulse" style={{ animationDelay: '0.3s' }} />
    </div>
    {/* Bottom nav skeleton */}
    <div className="h-[60px] flex items-center justify-around px-6 border-t border-white/[0.06]">
      {[0,1,2,3,4].map(i => <div key={i} className="w-6 h-6 rounded-md bg-white/[0.1]" />)}
    </div>
  </div>
);

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingShell />;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Public Route (redirect to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingShell />;
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Theme Provider
const ThemeProvider = ({ children }) => {
  // Resolve theme synchronously to prevent flash
  const getResolvedTheme = () => {
    const saved = localStorage.getItem('duolyst-theme') || 'system';
    if (saved === 'dark') return 'dark';
    if (saved === 'light') return 'light';
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  };

  const [themeMode, setThemeMode] = useState(() => {
    return localStorage.getItem('duolyst-theme') || 'system';
  });
  
  const [activeTheme, setActiveTheme] = useState(getResolvedTheme);

  // Apply theme class synchronously on mount (already set by inline script, but keep in sync)
  useEffect(() => {
    const root = document.documentElement;
    if (activeTheme === 'dark') {
      root.classList.add('dark');
      root.style.backgroundColor = '#1a1d21';
      document.body.style.backgroundColor = '#1a1d21';
    } else {
      root.classList.remove('dark');
      root.style.backgroundColor = '#F4F1DE';
      document.body.style.backgroundColor = '#F4F1DE';
    }
    // Update theme-color meta tag
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.content = activeTheme === 'dark' ? '#1a1d21' : '#F4F1DE';
  }, [activeTheme]);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e) => {
      // Only auto-switch if user is on system mode
      if (themeMode === 'system') {
        setActiveTheme(e.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [themeMode]);

  const setTheme = (mode) => {
    setThemeMode(mode);
    localStorage.setItem('duolyst-theme', mode);
    
    if (mode === 'system') {
      setActiveTheme(window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');
    } else {
      setActiveTheme(mode);
    }
  };

  const toggleTheme = () => {
    // Cycle: light -> dark -> system -> light
    if (themeMode === 'light') {
      setTheme('dark');
    } else if (themeMode === 'dark') {
      setTheme('system');
    } else {
      setTheme('light');
    }
  };

  const setSystemTheme = () => {
    setTheme('system');
  };

  return (
    <ThemeContext.Provider value={{ 
      theme: activeTheme, 
      themeMode,
      setTheme, 
      toggleTheme, 
      setSystemTheme 
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

function App({ onReady }) {
  // Signal ready once component tree mounts
  useEffect(() => {
    if (onReady) onReady();
    // Mark native app on <html> so CSS can differentiate
    if (window.isNativeApp) {
      document.documentElement.classList.add('native-app');
    }
  }, [onReady]);

  return (
    <ThemeProvider>
      <AuthProvider>
        <FeatureProvider>
        <GlobalRankThemeWrapper>
        <BrowserRouter>
          <LiveOpsRoot>
          <div className="min-h-screen bg-background" style={{ willChange: 'contents' }}>
            <Routes>
            <Route path="/" element={
              <PublicRoute>
                <Landing />
              </PublicRoute>
            } />
            <Route path="/login" element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            } />
            <Route path="/register" element={
              <PublicRoute>
                <Register />
              </PublicRoute>
            } />
            <Route path="/forgot-password" element={
              <PublicRoute>
                <ForgotPassword />
              </PublicRoute>
            } />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } />
            <Route path="/profile" element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            } />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/premium" element={<PricingPage />} />
            <Route path="/subscription/success" element={<SubscriptionSuccessPage />} />
            <Route path="/purchase/success" element={<PricingPage />} />
            <Route path="/feedback" element={
              <ProtectedRoute>
                <FeedbackPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/analytics" element={
              <ProtectedRoute>
                <AdminAnalytics />
              </ProtectedRoute>
            } />
            <Route path="/shared/:shareCode" element={<PublicTripSummary />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/explore" element={<ExplorePage />} />
            <Route path="/u/:username" element={<PublicProfile />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/founding-explorer" element={<FoundingExplorerPage />} />
            <Route path="/hall-of-fame" element={<HallOfFame />} />
            <Route path="/trip/:itemId" element={<ProtectedRoute><TripDetailsPage /></ProtectedRoute>} />
            <Route path="/paywall-demo" element={<PaywallDemo />} />
            <Route path="/invite/:token" element={<InviteLandingPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-center" richColors />
          <WhatsNewBanner />
          {/* InstallPrompt removed as requested */}
        </div>
        </LiveOpsRoot>
      </BrowserRouter>
      </GlobalRankThemeWrapper>
      </FeatureProvider>
    </AuthProvider>
    </ThemeProvider>
  );
}

// LiveOpsRoot — installs heartbeat + global error capture once per app lifetime.
// Lives inside <BrowserRouter> so useLocation works.
function LiveOpsRoot({ children }) {
  useLiveOps();
  // Global error capture — once per app
  useEffect(() => {
    const onError = (e) => {
      try {
        emitLiveError("uncaught", e?.error || new Error(e?.message || "window.onerror"), {
          filename: e?.filename,
          line: e?.lineno,
          col: e?.colno,
        });
      } catch (_) {}
    };
    const onRejection = (e) => {
      try {
        const reason = e?.reason || new Error("Unhandled promise rejection");
        emitLiveError("uncaught", reason, { kind: "unhandledrejection" });
      } catch (_) {}
    };
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);
  return <LiveOpsErrorBoundary>{children}</LiveOpsErrorBoundary>;
}

// Wrap children with rank-themed CSS vars based on the authenticated user.
function GlobalRankThemeWrapper({ children }) {
  const { user } = useAuth();
  return <GlobalRankTheme user={user}>{children}</GlobalRankTheme>;
}

export default App;
